import type { Link } from '../link';
import type { NetMessage } from '../protocol';

export interface RtcOptions {
  /** Optional STUN/TURN servers; LAN play works with none (host candidates). */
  iceServers: RTCIceServer[];
}

/**
 * WebRTC DataChannel link. One reliable ordered channel ("cmd") carries all
 * gameplay and lobby traffic. Works in Safari and Chrome.
 */
export class RtcLink implements Link {
  onMessage: ((m: unknown) => void) | null = null;
  onClose: (() => void) | null = null;
  rtt = 50;
  readonly pc: RTCPeerConnection;
  private channel: RTCDataChannel | null = null;
  private pending: string[] = [];
  private openResolve!: () => void;
  readonly opened: Promise<void>;
  remoteId: string;
  onIce: ((c: RTCIceCandidateInit) => void) | null = null;

  constructor(remoteId: string, opts: RtcOptions, initiator: boolean) {
    this.remoteId = remoteId;
    this.pc = new RTCPeerConnection({ iceServers: opts.iceServers });
    this.opened = new Promise((r) => (this.openResolve = r));
    this.pc.onicecandidate = (e) => {
      if (e.candidate) this.onIce?.(e.candidate.toJSON());
    };
    this.pc.onconnectionstatechange = () => {
      const s = this.pc.connectionState;
      if (s === 'failed' || s === 'closed') this.handleClose();
    };
    if (initiator) this.bind(this.pc.createDataChannel('cmd', { ordered: true }));
    else this.pc.ondatachannel = (e) => this.bind(e.channel);
  }

  private bind(ch: RTCDataChannel): void {
    this.channel = ch;
    ch.onopen = () => {
      for (const m of this.pending) ch.send(m);
      this.pending = [];
      this.openResolve();
    };
    ch.onmessage = (e) => {
      try {
        this.onMessage?.(JSON.parse(typeof e.data === 'string' ? e.data : new TextDecoder().decode(e.data)));
      } catch {
        /* drop malformed */
      }
    };
    ch.onclose = () => this.handleClose();
  }

  private closed = false;
  private handleClose(): void {
    if (this.closed) return;
    this.closed = true;
    this.onClose?.();
  }

  get open(): boolean {
    return !!this.channel && this.channel.readyState === 'open';
  }

  send(m: NetMessage): void {
    const s = JSON.stringify(m);
    if (this.open) this.channel!.send(s);
    else if (!this.closed) this.pending.push(s);
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const o = await this.pc.createOffer();
    await this.pc.setLocalDescription(o);
    return this.pc.localDescription!.toJSON();
  }

  private remoteSet = false;
  private iceQueue: RTCIceCandidateInit[] = [];

  private async flushIce(): Promise<void> {
    this.remoteSet = true;
    for (const c of this.iceQueue.splice(0)) await this.addIce(c);
  }

  async acceptOffer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    await this.pc.setRemoteDescription(offer);
    await this.flushIce();
    const a = await this.pc.createAnswer();
    await this.pc.setLocalDescription(a);
    return this.pc.localDescription!.toJSON();
  }

  async acceptAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(answer);
    await this.flushIce();
  }

  async addIce(c: RTCIceCandidateInit): Promise<void> {
    if (!this.remoteSet) {
      this.iceQueue.push(c);
      return;
    }
    try {
      await this.pc.addIceCandidate(c);
    } catch {
      /* ignore late candidates */
    }
  }

  /** Wait until ICE gathering completes (used for manual, non-trickle signalling). */
  waitIceComplete(timeoutMs = 4000): Promise<void> {
    if (this.pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
      const t = setTimeout(resolve, timeoutMs);
      this.pc.addEventListener('icegatheringstatechange', () => {
        if (this.pc.iceGatheringState === 'complete') {
          clearTimeout(t);
          resolve();
        }
      });
    });
  }

  close(): void {
    try {
      this.channel?.close();
      this.pc.close();
    } catch {
      /* ignore */
    }
    this.handleClose();
  }
}
